import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { Notification } from "../index"; // <-- your existing component
import ChangePasswordModal from "./ChangePasswordModal";
import TwoFactorAuthModal from "./TwoFactorAuthModal";
import "./layout.css"; // or header.css (whatever you use)
import "./logoutModal.css"; // logout modal styles
import { ReactComponent as LogoutIcon } from "../../images/LogoutNew.svg";
import { ReactComponent as RightArrow } from "../../images/RightArrow.svg";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";
import { getUserRole } from "../../utils/common";

const Header = ({
  title,
  userName,
  notifications,
  notificationsCount,
  collapsed,
  onHamburgerClick,
  onNotificationMenuClick,
  onNotificationLoadMore,
  notificationHasMore,
  notificationLoadingMore,
  onAccountDetails,
  onChangePassword,
  onLogout,
}) => {
  const navigate = useNavigate();
  const profileRef = React.useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
  const initial = userName?.charAt(0)?.toUpperCase() || "U";
  const { categoryTab, showCategoryChooser } = useClientCategoryTab();
  const userRole = getUserRole();
  const isClient = userRole === "client";
  const accountLabel =
    isClient && showCategoryChooser
      ? `${categoryTab === "business" ? "Business" : "Individual"} Account`
      : null;

  const handleLogoutClick = () => {
    setProfileOpen(false);
    setLogoutModalOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutModalOpen(false);
    onLogout();
  };

  const handleLogoutCancel = () => {
    setLogoutModalOpen(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="finlo-header">
      {/* LEFT: logo (aligned with sidebar column) */}
      <div className="finlo-header-left">
        <img
          key={collapsed ? "collapsed-logo" : "expanded-logo"}
          src={collapsed ? "/favicon.ico" : "/resources/images/Finlotax.png"}
          alt="FINLOTAX"
          className="finlo-header__logo"
          height="36"
        />
      </div>

      {/* Page title — starts where the sidebar ends */}
      {title ? (
        <div className="finlo-header-title-wrap">
          <span className="finlo-header-title">{title}</span>
        </div>
      ) : null}

      {/* RIGHT: notification bell + avatar + name + dropdown */}
      <div className="finlo-header-right">
        {/* Notification bell – uses your existing Notification component */}
        <div className="header-notification">
          <Notification
            badgeText={notificationsCount ? `${notificationsCount}` : ""}
            notifications={notifications}
            onMenuClick={onNotificationMenuClick}
            onLoadMore={onNotificationLoadMore}
            hasMore={notificationHasMore}
            loadingMore={notificationLoadingMore}
          />
        </div>

        <div className="header-profile" ref={profileRef}>
          <button
            type="button"
            className="header-profile-trigger"
            onClick={() => setProfileOpen((open) => !open)}
          >
            <div className="avatar-circle">{initial}</div>
            <div className="header-profile-meta">
              <span className="header-profile-name">{userName}</span>
              {accountLabel ? (
                <span className="header-profile-subtitle">{accountLabel}</span>
              ) : null}
            </div>
            <ArrowDropDownIcon
              className={`header-profile-caret ${profileOpen ? "open" : ""}`}
            />
          </button>

          {profileOpen && (
            <div className="profile-menu timesheet-view-modal">
              <div className="profile-menu-header">Profile</div>
              {(userRole === "employee" || userRole === "manager") && (
                <button
                  type="button"
                  className="profile-menu-item"
                  onClick={() => {
                    setProfileOpen(false);
                    navigate(`/finlo/${userRole}/profile`);
                  }}
                >
                  <span>Profile Details</span>
                  <span
                    style={{ marginRight: 8 }}
                    className="profile-menu-chevron"
                  >
                    <RightArrow />
                  </span>
                </button>
              )}
              {userRole === "client" &&
                localStorage.getItem("user_id") && (
                  <>
                    <button
                      type="button"
                      className="profile-menu-item"
                      onClick={onAccountDetails}
                    >
                      <span>Account Details</span>
                      <span
                        style={{ marginRight: 8 }}
                        className="profile-menu-chevron"
                      >
                        <RightArrow />
                      </span>
                    </button>
                    {/* Always show for clients so Individual-only accounts can
                        open select-account to add/switch to Business. */}
                    <button
                      type="button"
                      className="profile-menu-item"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate("/client/select-account");
                      }}
                    >
                      <span>Choose Account Type</span>
                      <span
                        style={{ marginRight: 8 }}
                        className="profile-menu-chevron"
                      >
                        <RightArrow />
                      </span>
                    </button>
                    {/* <button
                      type="button"
                      className="profile-menu-item"
                      onClick={() => {
                        setProfileOpen(false);
                        localStorage.removeItem("finlo_tour_completed");
                        localStorage.setItem("finlo_tour_step", "1");
                        window.location.reload();
                      }}
                    >
                      <span>Restart Onboarding Tour</span>
                      <span
                        style={{ marginRight: 8 }}
                        className="profile-menu-chevron"
                      >
                        <RightArrow />
                      </span>
                    </button> */}
                  </>
                )}

              <button
                type="button"
                className="profile-menu-item"
                onClick={() => {
                  setProfileOpen(false);
                  setTwoFactorModalOpen(true);
                }}
              >
                <span>Two Factor Authentication</span>
                <span
                  style={{ marginRight: 8 }}
                  className="profile-menu-chevron"
                >
                  <RightArrow />
                </span>
              </button>

              <button
                type="button"
                className="profile-menu-item"
                onClick={() => {
                  setProfileOpen(false);
                  setChangePasswordModalOpen(true);
                }}
              >
                <span>Change Password</span>
                <span
                  style={{ marginRight: 8 }}
                  className="profile-menu-chevron"
                >
                  <RightArrow />
                </span>
              </button>

              <button
                type="button"
                className="profile-menu-item logout"
                onClick={handleLogoutClick}
              >
                <span>Logout</span>
                <span className="profile-menu-logout-icon">
                  <LogoutIcon />
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {logoutModalOpen && (
        <div className="logout-modal-overlay" onClick={handleLogoutCancel}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="logout-modal-title">Logout</h2>
            <p className="logout-modal-question">Are you sure want logout?</p>
            <div className="logout-modal-buttons">
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-no"
                onClick={handleLogoutCancel}
              >
                No
              </button>
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-yes"
                onClick={handleLogoutConfirm}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
      />

      <TwoFactorAuthModal
        open={twoFactorModalOpen}
        onClose={() => setTwoFactorModalOpen(false)}
      />
    </header>
  );
};

export default Header;
